import { useState, useEffect, useCallback, useMemo } from "react"
import { useParams } from "react-router-dom"
import { subDays, differenceInDays, addDays } from "date-fns"
import { BiDollarCircle } from "react-icons/bi"
import { TbCalendarDollar } from "react-icons/tb"
import { MdQueryStats, MdOutlineIntegrationInstructions } from "react-icons/md"
import { RiDonutChartFill } from "react-icons/ri"
import { IconInfoCircle } from "@tabler/icons-react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Skeleton } from "@/components/ui/skeleton"
import { FilterContainer } from "@/components/FiltersV2"
import { CustomDatePicker } from "@/components/CustomDatePicker"
import { ModelsSelector } from "@/components/ModelsSelector"
import { PageContent } from "@/components/PageContent"
import { DashboardCard, TotalSpend, LastXDaysSpend, ProvidersBreakdown, HorizontalBarChart, UsageChart, SpendChart, SaveDashboardModal } from "@/components/Dashboard"
import { InlineMetadataFilters, type AppliedFilter } from "@/components/FiltersSelector"
import { useCustomDashboard } from "@/contexts/CustomDashboardContext"
import { useBreakdownTimeSeries } from "@/data/hooks/api/useBreakdown"
import { useTotalSpend, useProvidersBreakdown, useContributorsSpend, useSpend, useCustomDashboardDetail } from "@/data/hooks/api/useDashboards"
import type { BreakdownTimeSeriesRequest } from "@/data/types/breakdown.types"
import type { ModelSelection } from "@/data/types/analytics.types"
import type { TotalSpendRequest, TotalSpendMetadataFilter, ProvidersBreakdownRequest, ProvidersBreakdownMetadataFilter, ContributorsSpendRequest, ContributorsSpendMetadataFilter, SpendRequest, SpendMetadataFilter, CustomDashboardResponse } from "@/data/types/dashboard.types"
import type { DateRange } from "react-day-picker"
import type { DashboardFilter } from "./CustomDashboardPage"
import { getRelativeDateRange } from "@/lib/utils"

// Default selection for models
const ALL_MODELS_SELECTION: ModelSelection = { all: true, providers: [], models: [] }

// Convert between DashboardFilter and AppliedFilter formats
const dashboardFiltersToAppliedFilters = (filters: DashboardFilter[]): AppliedFilter[] => {
  return filters.map(f => ({
    id: f.id,
    tagName: f.tagName,
    tagNameLabel: f.tagNameLabel,
    operation: f.operation === "equals" ? "is" : f.operation === "not equals" ? "is_not" : f.operation === "not like" ? "not_like" : f.operation as "is" | "is_not" | "like" | "not_like",
    operationLabel: f.operation === "equals" ? "is" : f.operation === "not equals" ? "is not" : f.operation === "not like" ? "not like" : f.operationLabel,
    value: f.value,
  }))
}

const appliedFiltersToDashboardFilters = (filters: AppliedFilter[]): DashboardFilter[] => {
  return filters.map(f => ({
    id: f.id,
    tagName: f.tagName,
    tagNameLabel: f.tagNameLabel,
    operation: f.operation === "is" ? "equals" : f.operation === "is_not" ? "not equals" : f.operation === "not_like" ? "not like" : f.operation,
    operationLabel: f.operation === "is" ? "equals" : f.operation === "is_not" ? "not equals" : f.operation === "not_like" ? "not like" : f.operationLabel,
    value: f.value,
  }))
}

export function CustomDashboardViewPage() {
  const { id } = useParams<{ id: string }>()
  const [dashboard, setDashboard] = useState<CustomDashboardResponse | null>(null)

  // Check if this is a new dashboard creation
  const isNewDashboard = id === "new"

  // Fetch dashboard detail if not new
  const { data: dashboardDetail, isLoading: isLoadingDashboard } = useCustomDashboardDetail(id || "", !isNewDashboard)

  // Use context for shared state with site-header
  const {
    dashboardFilters,
    setDashboardFilters,
    initializeDashboard,
    clearDashboard,
  } = useCustomDashboard()

  // Initialize from API data
  useEffect(() => {
    if (dashboardDetail) {
      setDashboard(dashboardDetail)

      // Convert API filters to UI filters
      const uiFilters: DashboardFilter[] = (dashboardDetail.filters.metadata_filters || []).map((f, index) => ({
        id: `f-${index}`,
        tagName: f.key,
        tagNameLabel: f.key,
        operation: f.operation === 'is' ? 'equals' : 'not equals',
        operationLabel: f.operation === 'is' ? 'equals' : 'not equals',
        value: f.value
      }))

      initializeDashboard(
        dashboardDetail.id,
        dashboardDetail.name,
        dashboardDetail.description || "",
        uiFilters,
        dashboardDetail.is_recommended,
        dashboardDetail.filters.relative_date
      )

      // Apply relative date if present
      if (dashboardDetail.filters.relative_date) {
        const range = getRelativeDateRange(dashboardDetail.filters.relative_date)
        setAppliedDateRange(range)
        setWorkingDateRange(range)
      }

      // Apply model selection if present
      if (dashboardDetail.filters.model_selection) {
        setModelSelection(dashboardDetail.filters.model_selection)
      }
    }
  }, [dashboardDetail, initializeDashboard])

  // Timeseries data setup - default last 30 days, daily granularity, modality grouping
  const DEFAULT_GRANULARITY = 'daily' as const
  const DEFAULT_GROUP_BY = 'modality'
  const MAX_CHART_DATE_RANGE_DAYS = 90

  // Date range state - working state for the picker, applied state for API requests
  const [workingDateRange, setWorkingDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 29),
    to: new Date(),
  })
  const [appliedDateRange, setAppliedDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 29),
    to: new Date(),
  })

  // Models selection state
  const [modelSelection, setModelSelection] = useState<ModelSelection>(ALL_MODELS_SELECTION)

  // Handle date range apply from picker - no cap on full page, only on chart component
  const handleDateRangeApply = useCallback((range: DateRange | undefined) => {
    setAppliedDateRange(range)
    setWorkingDateRange(range)
  }, [])

  // Calculate the actual days in selected range
  const selectedDays = useMemo(() => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return 0
    return differenceInDays(appliedDateRange.to, appliedDateRange.from) + 1
  }, [appliedDateRange])

  // Check if the date range exceeds the chart limit
  const isChartDateRangeCapped = selectedDays > MAX_CHART_DATE_RANGE_DAYS

  // Calculate the capped date range for the chart component
  const chartDateRange = useMemo((): DateRange | undefined => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return appliedDateRange

    if (isChartDateRangeCapped) {
      return {
        from: appliedDateRange.from,
        to: addDays(appliedDateRange.from, MAX_CHART_DATE_RANGE_DAYS - 1)
      }
    }
    return appliedDateRange
  }, [appliedDateRange, isChartDateRangeCapped])

  // Calculate days displayed in the chart
  const chartDisplayDays = isChartDateRangeCapped ? MAX_CHART_DATE_RANGE_DAYS : selectedDays

  // Dynamic title for the Last X Days Spend card
  const lastXDaysTitle = (
    <span className="flex items-center gap-1.5">
      Last {chartDisplayDays} Days Spend
      {isChartDateRangeCapped && (
        <TooltipPrimitive.Provider delayDuration={200}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <span className="inline-flex">
                <IconInfoCircle className="size-4 text-[#8A8F98]" />
              </span>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side="top"
                align="center"
                sideOffset={6}
                className="z-50 min-w-[160px] max-w-[280px] bg-white border border-[#EDEEF3] rounded-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] p-[12px] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 origin-[var(--radix-tooltip-content-transform-origin)]"
              >
                <p className="text-[12px] text-[#08090A] leading-[18px] m-0">
                  Chart limited to {MAX_CHART_DATE_RANGE_DAYS} days. You selected {selectedDays} days.
                </p>
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      )}
    </span>
  )

  // Convert dashboard filters to API format for timeseries
  const metadataFiltersForApi = useMemo(() => {
    return dashboardFilters
      .filter((f) => f.tagName && f.operation && f.value)
      .map((f) => ({
        key: f.tagName,
        operator: (f.operation === 'equals' || f.operation === 'is' ? 'eq' : 'neq') as 'eq' | 'neq',
        value: f.value,
      }))
  }, [dashboardFilters])

  // Build API request for timeseries (uses capped chart date range)
  const timeSeriesRequest = useMemo((): BreakdownTimeSeriesRequest | null => {
    if (!chartDateRange?.from || !chartDateRange?.to) return null

    const startDate = new Date(chartDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(chartDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    const hasModelSelection = modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0

    return {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString(),
      granularity: DEFAULT_GRANULARITY,
      group_by: DEFAULT_GROUP_BY,
      model_selection: hasModelSelection ? modelSelection : null,
      providers: null,
      metadata: metadataFiltersForApi.length > 0 ? metadataFiltersForApi : null,
    }
  }, [chartDateRange, modelSelection, metadataFiltersForApi])

  const { data: timeSeriesResponse, isLoading: isLoadingTimeSeries } = useBreakdownTimeSeries(
    timeSeriesRequest,
    timeSeriesRequest !== null
  )

  // Convert dashboard filters to API format for total spend
  const metadataFiltersForTotalSpend = useMemo((): TotalSpendMetadataFilter[] | null => {
    const validFilters = dashboardFilters.filter((f) => f.tagName && f.operation && f.value)
    if (validFilters.length === 0) return null

    return validFilters.map((f) => ({
      key: f.tagName,
      operation: (f.operation === 'equals' || f.operation === 'is' ? 'is' : 'is_not') as 'is' | 'is_not',
      value: f.value,
    }))
  }, [dashboardFilters])

  // Convert dashboard filters to API format for providers breakdown
  const metadataFiltersForProvidersBreakdown = useMemo((): ProvidersBreakdownMetadataFilter[] | null => {
    const validFilters = dashboardFilters.filter((f) => f.tagName && f.operation && f.value)
    if (validFilters.length === 0) return null

    return validFilters.map((f) => ({
      key: f.tagName,
      operation: (f.operation === 'equals' || f.operation === 'is' ? 'is' : 'is_not') as 'is' | 'is_not',
      value: f.value,
    }))
  }, [dashboardFilters])

  // Convert dashboard filters to API format for contributors spend
  const metadataFiltersForContributorsSpend = useMemo((): ContributorsSpendMetadataFilter[] | null => {
    const validFilters = dashboardFilters.filter((f) => f.tagName && f.operation && f.value)
    if (validFilters.length === 0) return null

    return validFilters.map((f) => ({
      key: f.tagName,
      operation: (f.operation === 'equals' || f.operation === 'is' ? 'is' : 'is_not') as 'is' | 'is_not',
      value: f.value,
    }))
  }, [dashboardFilters])

  // Convert dashboard filters to API format for spend (usage & spend charts)
  const metadataFiltersForSpend = useMemo((): SpendMetadataFilter[] | null => {
    const validFilters = dashboardFilters.filter((f) => f.tagName && f.operation && f.value)
    if (validFilters.length === 0) return null

    return validFilters.map((f) => ({
      key: f.tagName,
      operation: (f.operation === 'equals' || f.operation === 'is' ? 'is' : 'is_not') as 'is' | 'is_not',
      value: f.value,
    }))
  }, [dashboardFilters])

  // Build API request for total spend
  const totalSpendRequest = useMemo((): TotalSpendRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null

    const startDate = new Date(appliedDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(appliedDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    const hasModelSelection = modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: hasModelSelection ? modelSelection : null,
      providers: null,
      metadata_filters: metadataFiltersForTotalSpend,
    }
  }, [appliedDateRange, modelSelection, metadataFiltersForTotalSpend])

  const { data: totalSpendData, isLoading: isLoadingTotalSpend } = useTotalSpend(
    totalSpendRequest,
    totalSpendRequest !== null
  )

  // Build API request for providers breakdown
  const providersBreakdownRequest = useMemo((): ProvidersBreakdownRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null

    const startDate = new Date(appliedDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(appliedDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    const hasModelSelection = modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: hasModelSelection ? modelSelection : null,
      providers: null,
      metadata_filters: metadataFiltersForProvidersBreakdown,
    }
  }, [appliedDateRange, modelSelection, metadataFiltersForProvidersBreakdown])

  const { data: providersBreakdownData, isLoading: isLoadingProvidersBreakdown } = useProvidersBreakdown(
    providersBreakdownRequest,
    providersBreakdownRequest !== null
  )

  // Build API request for contributors spend
  const contributorsSpendRequest = useMemo((): ContributorsSpendRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null

    const startDate = new Date(appliedDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(appliedDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      metadata_filters: metadataFiltersForContributorsSpend,
    }
  }, [appliedDateRange, metadataFiltersForContributorsSpend])

  const { data: contributorsSpendData, isLoading: isLoadingContributorsSpend } = useContributorsSpend(
    contributorsSpendRequest,
    contributorsSpendRequest !== null
  )

  // Build API request for spend (usage & spend charts)
  const spendRequest = useMemo((): SpendRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null

    const startDate = new Date(appliedDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(appliedDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    const hasModelSelection = modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: hasModelSelection ? modelSelection : null,
      providers: null,
      metadata_filters: metadataFiltersForSpend,
    }
  }, [appliedDateRange, modelSelection, metadataFiltersForSpend])

  const { data: spendData, isLoading: isLoadingSpend } = useSpend(
    spendRequest,
    spendRequest !== null
  )

  useEffect(() => {
    if (!id) return

    // Handle new dashboard creation
    if (isNewDashboard) {
      // Initialize with placeholder data
      initializeDashboard("new", "Untitled Dashboard", "Add a description for your dashboard", [])
      setDashboard({
        id: "new",
        name: "Untitled Dashboard",
        description: "Add a description for your dashboard",
        is_recommended: false,
        display_order: 0,
        is_starred: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        filters: {},
      } as CustomDashboardResponse)
      return
    }

    // Clear context when leaving the page
    return () => {
      clearDashboard()
    }
  }, [id, initializeDashboard, clearDashboard, isNewDashboard])

  // Handle filter changes from InlineMetadataFilters
  const handleFiltersChange = useCallback((filters: AppliedFilter[]) => {
    setDashboardFilters(appliedFiltersToDashboardFilters(filters))
  }, [setDashboardFilters])

  if (isLoadingDashboard || (isNewDashboard && !dashboard)) {
    return (
      <div className="flex flex-col">
        {/* Loading skeleton */}
        <FilterContainer>
          <div className="flex gap-2">
            <Skeleton className="h-[30px] w-[180px] rounded-[6px]" />
            <Skeleton className="h-[30px] w-[150px] rounded-[6px]" />
          </div>
        </FilterContainer>
        <FilterContainer>
          <div className="flex gap-2">
            <Skeleton className="h-[30px] w-[180px] rounded-[6px]" />
            <Skeleton className="h-[30px] w-[150px] rounded-[6px]" />
          </div>
        </FilterContainer>
        <PageContent>
          <div className="space-y-4">
            <div className="grid grid-cols-[4fr_4fr] gap-4">
              <Skeleton className="h-[300px] rounded-[8px]" />
              <Skeleton className="h-[300px] rounded-[8px]" />
            </div>
            <Skeleton className="h-[400px] rounded-[8px]" />
          </div>
        </PageContent>
      </div>
    )
  }

  if (!dashboard) {
    return null
  }

  return (
    <div className="flex flex-col">
      <SaveDashboardModal modelSelection={modelSelection} />
      {/* Date picker and models selector */}
      <FilterContainer>
        <div className="flex items-center gap-2">
          <CustomDatePicker
            value={workingDateRange}
            onChange={setWorkingDateRange}
            onApply={handleDateRangeApply}
          />
          <ModelsSelector value={modelSelection} onChange={setModelSelection} />
        </div>
      </FilterContainer>

      {/* Metadata filters - using same InlineMetadataFilters component as breakdown page */}
      <FilterContainer>
        <InlineMetadataFilters
          value={dashboardFiltersToAppliedFilters(dashboardFilters)}
          onChange={handleFiltersChange}
          addButtonLabel="Filters"
        />
      </FilterContainer>

      {/* Dashboard content - same as Overview page */}
      <PageContent>
        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-[3fr_4fr] gap-4">
            <DashboardCard
              icon={<BiDollarCircle />}
              title="Total Spend"
              subtitle="Your cumulative AI spending across all providers and models. Track this to understand your overall budget consumption and identify spending trends over time."
            >
              <TotalSpend data={totalSpendData} isLoading={isLoadingTotalSpend} />
            </DashboardCard>
            <DashboardCard
              icon={<TbCalendarDollar />}
              title={lastXDaysTitle}
              subtitle="A breakdown of your AI costs over the selected period by modality (text, image, audio, video). Use this to spot daily spending patterns and identify which modalities are driving costs."
            >
              <LastXDaysSpend
                data={timeSeriesResponse?.data || []}
                granularity={DEFAULT_GRANULARITY}
                dateRange={chartDateRange}
                isLoading={isLoadingTimeSeries}
              />
            </DashboardCard>
          </div>
          <div className="grid grid-cols-[3fr_3fr] gap-4">
            <DashboardCard
              icon={<RiDonutChartFill />}
              title="Top Contributors to AI Spend"
              subtitle="Identify which teams, projects, or users are generating the most AI costs. Essential for cost allocation, budget planning, and ensuring resources are being used efficiently across your organization."
            >
              <HorizontalBarChart data={contributorsSpendData} isLoading={isLoadingContributorsSpend} />
            </DashboardCard>
            <DashboardCard
              icon={<MdOutlineIntegrationInstructions />}
              title="Providers Breakdown"
              subtitle="Compare costs across AI providers (OpenAI, Anthropic, Google, etc.) to optimize your provider mix. Track average costs and usage trends to negotiate better rates or shift workloads to more cost-effective alternatives."
            >
              <ProvidersBreakdown data={providersBreakdownData} isLoading={isLoadingProvidersBreakdown} />
            </DashboardCard>
          </div>
          {/* Row 2 */}
          <div className="grid grid-cols-[1fr_1fr] gap-4">
            <DashboardCard
              icon={<MdQueryStats />}
              title="Usage"
              subtitle="Monitor total events across providers to track usage patterns and identify trends. Deviations in usage growth may indicate changes in product adoption, user behavior, or integration issues that should be reviewed."
            >
              <UsageChart data={spendData} isLoading={isLoadingSpend} />
            </DashboardCard>
            <DashboardCard
              icon={<MdQueryStats />}
              title="Spend"
              subtitle="Track average cost per action to understand spending efficiency. Costs should scale proportionally with usage. Faster cost growth, gradual upward drift, or sudden spikes may point to inefficiencies or model changes that need attention."
            >
              <SpendChart data={spendData} isLoading={isLoadingSpend} />
            </DashboardCard>
          </div>
        </div>
      </PageContent>

    </div>
  )
}

